import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Select, Slider, Radio, InputNumber, Input, message } from 'antd';
import chatStore from '../stores/chatStore';
import {
    DEFAULT_ASPECT_RATIOS,
    aspectsEqual,
    createAspectId,
    normalizeAspectList,
    normalizeAspectRatio,
} from '../utils/aspectRatios';

const GptSettingsPanel = observer(({ onClose }) => {
    const s = chatStore.settings;
    const [rateDraft, setRateDraft] = useState(s.usd_rub_rate ?? 90);
    const [aspectDrafts, setAspectDrafts] = useState(() =>
        normalizeAspectList(s.aspect_ratios)
    );
    const [newRatio, setNewRatio] = useState('');
    const [newName, setNewName] = useState('');
    const [savingAspects, setSavingAspects] = useState(false);

    useEffect(() => {
        setRateDraft(s.usd_rub_rate ?? 90);
    }, [s.usd_rub_rate]);

    useEffect(() => {
        setAspectDrafts(normalizeAspectList(s.aspect_ratios));
    }, [s.aspect_ratios]);

    const save = async (patch) => {
        try {
            await chatStore.saveSettings(patch);
            const uiOnly = Object.keys(patch || {}).every((k) =>
                [
                    'theme',
                    'message_font_size',
                    'chat_list_font_size',
                    'button_scale',
                    'image_preview_size',
                ].includes(k)
            );
            message.success(uiOnly ? 'Сохранено на этом устройстве' : 'Сохранено');
        } catch (e) {
            message.error(e.gptError?.message || e.message || 'Ошибка сохранения');
        }
    };

    /** Controlled Slider в antd требует onChange, иначе ползунок не двигается */
    const setLocalSetting = (key, value) => {
        chatStore.settings[key] = value;
    };

    const saveAspects = async (next) => {
        const cleaned = normalizeAspectList(next);
        if (!cleaned.length) {
            message.error('Нужно хотя бы одно соотношение сторон');
            return;
        }
        setAspectDrafts(cleaned);
        if (aspectsEqual(cleaned, s.aspect_ratios)) return;
        setSavingAspects(true);
        try {
            await chatStore.saveSettings({ aspect_ratios: cleaned });
            message.success('Соотношения сохранены');
        } catch (e) {
            message.error(e.gptError?.message || e.message || 'Ошибка сохранения');
            setAspectDrafts(normalizeAspectList(s.aspect_ratios));
        } finally {
            setSavingAspects(false);
        }
    };

    const updateDraftField = (index, field, value) => {
        setAspectDrafts((prev) =>
            prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        );
    };

    const onAspectBlur = (index) => {
        const item = aspectDrafts[index];
        const ratio = normalizeAspectRatio(item.ratio);
        if (!ratio) {
            message.error('Формат: ширина:высота, например 21:9');
            setAspectDrafts(normalizeAspectList(s.aspect_ratios));
            return;
        }
        const next = aspectDrafts.map((row, i) =>
            i === index
                ? {
                      id: row.id || createAspectId(),
                      ratio,
                      name: String(item.name || '').trim(),
                  }
                : {
                      id: row.id || createAspectId(),
                      ratio: row.ratio,
                      name: String(row.name || '').trim(),
                  }
        );
        saveAspects(next);
    };

    const addAspect = () => {
        const ratio = normalizeAspectRatio(newRatio);
        if (!ratio) {
            message.error('Формат: ширина:высота, например 21:9');
            return;
        }
        const name = String(newName || '').trim();
        setNewRatio('');
        setNewName('');
        saveAspects([...aspectDrafts, { id: createAspectId(), ratio, name }]);
    };

    const removeAspect = (index) => {
        if (aspectDrafts.length <= 1) {
            message.error('Нужно хотя бы одно соотношение сторон');
            return;
        }
        saveAspects(aspectDrafts.filter((_, i) => i !== index));
    };

    const moveAspect = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= aspectDrafts.length) return;
        const next = [...aspectDrafts];
        const [item] = next.splice(index, 1);
        next.splice(target, 0, item);
        saveAspects(next);
    };

    const resetAspects = () => {
        saveAspects(DEFAULT_ASPECT_RATIOS.map((item) => ({ ...item })));
    };

    return (
        <div className="space-y-4">
            <div>
                <div className="mb-1 text-sm font-medium">Внешний вид на этом устройстве</div>
                <div className="mb-3 text-xs opacity-55">
                    Тема, шрифты и масштабы хранятся в браузере — отдельно для каждого монитора и
                    разрешения.
                </div>

                <div className="mb-1 text-sm font-medium">Тема</div>
                <Radio.Group
                    value={s.theme || 'system'}
                    onChange={(e) => save({ theme: e.target.value })}
                    options={[
                        { label: 'Системная', value: 'system' },
                        { label: 'Светлая', value: 'light' },
                        { label: 'Тёмная', value: 'dark' },
                    ]}
                />
            </div>

            <div>
                <div className="mb-1 text-sm font-medium">
                    Шрифт сообщений: {Number(s.message_font_size ?? 15)}px
                </div>
                <Slider
                    min={12}
                    max={24}
                    step={1}
                    value={Number(s.message_font_size ?? 15)}
                    onChange={(v) => setLocalSetting('message_font_size', v)}
                    onChangeComplete={(v) => save({ message_font_size: v })}
                />
            </div>

            <div>
                <div className="mb-1 text-sm font-medium">
                    Шрифт списка чатов: {Number(s.chat_list_font_size ?? 14)}px
                </div>
                <Slider
                    min={11}
                    max={20}
                    step={1}
                    value={Number(s.chat_list_font_size ?? 14)}
                    onChange={(v) => setLocalSetting('chat_list_font_size', v)}
                    onChangeComplete={(v) => save({ chat_list_font_size: v })}
                />
            </div>

            <div>
                <div className="mb-1 text-sm font-medium">
                    Масштаб кнопок: {Number(s.button_scale ?? 1).toFixed(2)}×
                </div>
                <Slider
                    min={0.75}
                    max={1.5}
                    step={0.05}
                    value={Number(s.button_scale ?? 1)}
                    onChange={(v) => setLocalSetting('button_scale', v)}
                    onChangeComplete={(v) => save({ button_scale: v })}
                />
                <div className="mt-1 text-xs opacity-55">
                    Действует на кнопки действий в сообщениях, изображениях и панели ввода.
                </div>
            </div>

            <div>
                <div className="mb-1 text-sm font-medium">
                    Превью изображений: {Number(s.image_preview_size ?? 480)}px
                </div>
                <Slider
                    min={200}
                    max={960}
                    step={20}
                    value={Number(s.image_preview_size ?? 480)}
                    onChange={(v) => setLocalSetting('image_preview_size', v)}
                    onChangeComplete={(v) => save({ image_preview_size: v })}
                />
                <div className="mt-1 text-xs opacity-55">
                    Максимальный размер превью отправленных и сгенерированных картинок в ленте чата.
                </div>
            </div>

            <div>
                <div className="mb-1 text-sm font-medium">Temperature: {Number(s.temperature ?? 0.7).toFixed(1)}</div>
                <Slider
                    min={0}
                    max={2}
                    step={0.1}
                    value={Number(s.temperature ?? 0.7)}
                    onChange={(v) => setLocalSetting('temperature', v)}
                    onChangeComplete={(v) => save({ temperature: v })}
                />
            </div>

            <div>
                <div className="mb-1 text-sm font-medium">Курс доллара (₽ / $)</div>
                <div className="flex items-center gap-2">
                    <InputNumber
                        className="w-full"
                        min={1}
                        max={1000}
                        step={0.1}
                        value={rateDraft}
                        onChange={(v) => setRateDraft(v ?? 90)}
                        addonAfter="₽"
                    />
                    <button
                        type="button"
                        className="shrink-0 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white"
                        onClick={() => save({ usd_rub_rate: Number(rateDraft) || 90 })}
                    >
                        Сохранить
                    </button>
                </div>
                <div className="mt-1 text-xs opacity-55">
                    Стоимость запросов и моделей будет показана в рублях по этому курсу.
                </div>
            </div>

            <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                    <div className="text-sm font-medium">Соотношения сторон</div>
                    <button
                        type="button"
                        className="text-xs opacity-60 hover:opacity-100"
                        onClick={resetAspects}
                        disabled={savingAspects}
                    >
                        Сбросить
                    </button>
                </div>
                <div className="mb-1 grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_auto] gap-2 text-[11px] opacity-50">
                    <span className="w-14 text-center">Порядок</span>
                    <span>Соотношение</span>
                    <span>Название</span>
                    <span className="w-16" />
                </div>
                <div className="space-y-2">
                    {aspectDrafts.map((item, index) => (
                        <div
                            key={item.id || `aspect-${index}`}
                            className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_auto] items-center gap-2"
                        >
                            <div className="flex w-14 items-center justify-center gap-0.5">
                                <button
                                    type="button"
                                    className="rounded px-1.5 py-1 text-xs opacity-60 hover:bg-black/5 hover:opacity-100 disabled:opacity-25 dark:hover:bg-white/10"
                                    onClick={() => moveAspect(index, -1)}
                                    disabled={savingAspects || index === 0}
                                    title="Выше"
                                >
                                    ↑
                                </button>
                                <button
                                    type="button"
                                    className="rounded px-1.5 py-1 text-xs opacity-60 hover:bg-black/5 hover:opacity-100 disabled:opacity-25 dark:hover:bg-white/10"
                                    onClick={() => moveAspect(index, 1)}
                                    disabled={savingAspects || index === aspectDrafts.length - 1}
                                    title="Ниже"
                                >
                                    ↓
                                </button>
                            </div>
                            <Input
                                value={item.ratio}
                                onChange={(e) => updateDraftField(index, 'ratio', e.target.value)}
                                onBlur={() => onAspectBlur(index)}
                                onPressEnter={(e) => e.currentTarget.blur()}
                                placeholder="16:9"
                                disabled={savingAspects}
                            />
                            <Input
                                value={item.name || ''}
                                onChange={(e) => updateDraftField(index, 'name', e.target.value)}
                                onBlur={() => onAspectBlur(index)}
                                onPressEnter={(e) => e.currentTarget.blur()}
                                placeholder="Необязательно"
                                disabled={savingAspects}
                            />
                            <button
                                type="button"
                                className="w-16 shrink-0 rounded-lg px-2 py-1.5 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-40"
                                onClick={() => removeAspect(index)}
                                disabled={savingAspects || aspectDrafts.length <= 1}
                                title="Удалить"
                            >
                                Удалить
                            </button>
                        </div>
                    ))}
                    <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_auto] items-center gap-2">
                        <div className="w-14" />
                        <Input
                            value={newRatio}
                            onChange={(e) => setNewRatio(e.target.value)}
                            onPressEnter={addAspect}
                            placeholder="21:9"
                            disabled={savingAspects}
                        />
                        <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onPressEnter={addAspect}
                            placeholder="Название"
                            disabled={savingAspects}
                        />
                        <button
                            type="button"
                            className="w-16 shrink-0 rounded-lg bg-emerald-600 px-2 py-1.5 text-sm text-white disabled:opacity-40"
                            onClick={addAspect}
                            disabled={savingAspects || !newRatio.trim()}
                        >
                            +
                        </button>
                    </div>
                </div>
                <div className="mt-1 text-xs opacity-55">
                    Если указано название — оно показывается в списке «Формат». Порядок сохраняется.
                </div>
            </div>

            <div>
                <div className="mb-1 text-sm font-medium">PDF parser</div>
                <Select
                    className="w-full"
                    value={s.pdf_parser || 'auto'}
                    options={[
                        { value: 'auto', label: 'auto' },
                        { value: 'cloudflare-ai', label: 'cloudflare-ai' },
                        { value: 'mistral-ocr', label: 'mistral-ocr' },
                        { value: 'native', label: 'native' },
                    ]}
                    onChange={(v) => save({ pdf_parser: v })}
                />
            </div>

            <div className="text-xs opacity-60">
                API-ключ OpenRouter настраивается в разделе «Профиль».
            </div>

            <button
                type="button"
                className="rounded-lg bg-black/80 px-3 py-2 text-sm text-white dark:bg-white/20"
                onClick={async () => {
                    try {
                        await chatStore.loadModels();
                        await chatStore.loadImageModels();
                        message.success('Список моделей обновлён');
                    } catch (e) {
                        message.error(e.gptError?.message || 'Не удалось загрузить модели');
                    }
                }}
            >
                Обновить модели
            </button>
        </div>
    );
});

export default GptSettingsPanel;
